// src/components/charts/OverallEmissionsLineChart.js

import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

// --- helpers ---
function safeNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

// ✅ choose the correct date for charting
function getChartDate(h) {
  return h?.activity_date || h?.date || h?.uploaded_at || null;
}

function toDateKey(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateLabel(yyyy_mm_dd) {
  const [yyyy, mm, dd] = (yyyy_mm_dd || "").split("-");
  if (!yyyy || !mm || !dd) return yyyy_mm_dd || "";
  return `${dd}/${mm}/${yyyy}`;
}

// Linear regression (least squares) for trendline
function addTrendline(points) {
  const n = points.length;
  if (n < 2) return null;

  let sumX = 0,
    sumY = 0,
    sumXY = 0,
    sumXX = 0;

  for (const p of points) {
    sumX += p.idx;
    sumY += p.y;
    sumXY += p.idx * p.y;
    sumXX += p.idx * p.idx;
  }

  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return null;

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
}

/**
 * Props:
 *  - history: [{ uploaded_at, activity_date?, date?, total_kgco2e, filename? }, ...]
 *  - mode: "perUpload" | "cumulative"  (default cumulative)
 *  - captureId?: string  (optional) -> gives the wrapper a stable DOM id for PNG capture
 */
export default function OverallEmissionsLineChart({
  history = [],
  mode = "cumulative",
  captureId,
}) {
  const data = useMemo(() => {
    if (!Array.isArray(history) || history.length === 0) return [];

    // 1) normalize + sort by chart date
    const cleaned = history
      .map((h) => {
        const key = toDateKey(getChartDate(h));
        if (!key) return null;
        return { dateKey: key, value: safeNumber(h.total_kgco2e) };
      })
      .filter(Boolean)
      .sort((a, b) => (a.dateKey > b.dateKey ? 1 : a.dateKey < b.dateKey ? -1 : 0));

    if (cleaned.length === 0) return [];

    // 2) aggregate by day (sum on same day)
    const byDay = new Map();
    for (const row of cleaned) {
      byDay.set(row.dateKey, (byDay.get(row.dateKey) || 0) + row.value);
    }

    const series = Array.from(byDay.entries())
      .sort((a, b) => (a[0] > b[0] ? 1 : -1))
      .map(([dateKey, dayTotal]) => ({ dateKey, dayTotal: safeNumber(dayTotal) }));

    // 3) build chart series
    let running = 0;
    const chart = series.map((d, idx) => {
      running += d.dayTotal;
      return {
        idx,
        dateKey: d.dateKey,
        label: formatDateLabel(d.dateKey),
        emissions: mode === "perUpload" ? d.dayTotal : running,
      };
    });

    // 4) trendline
    const pts = chart.map((d) => ({ idx: d.idx, y: safeNumber(d.emissions) }));
    const model = addTrendline(pts);

    if (model) {
      for (const d of chart) d.trend = model.slope * d.idx + model.intercept;
    } else {
      for (const d of chart) d.trend = null;
    }

    return chart;
  }, [history, mode]);

  if (!data.length) {
    return (
      <div style={{ padding: 10, opacity: 0.8 }}>
        No history available yet. Upload documents to generate a trend.
      </div>
    );
  }

  return (
    <div id={captureId} style={{ width: "100%", height: 280 }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} minTickGap={18} />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${Math.round(v)}`} />
          <Tooltip
            formatter={(value, name) => {
              if (name === "emissions") return [`${Math.round(value)} kg CO₂e`, "Emissions"];
              if (name === "trend") return [`${Math.round(value)} kg CO₂e`, "Trend"];
              return [value, name];
            }}
            labelFormatter={(label) => `Date: ${label}`}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="emissions"
            name={mode === "perUpload" ? "Daily total" : "Cumulative total"}
            strokeWidth={3}
            stroke="#34af1b"
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="trend"
            name="Trend"
            strokeWidth={2}
            stroke="#1b5e0d"
            dot={false}
            strokeDasharray="6 6"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
